/**
 * Phase 322a — Onboarding handoff page.
 *
 * The 24h magic link from the welcome email lands here. We:
 *   1. Extract `token` from the URL.
 *   2. POST it to /api/onboarding/exchange-magic-link.
 *   3. Cache the access_token + user into localStorage exactly as the
 *      normal login flow does (so navigating to /dashboard works).
 *   4. Show a brief welcome screen with a "Continue to Dashboard" CTA.
 *      (The full 4-step wizard ships in the next phase — for now we just
 *      get the owner safely logged in.)
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowRight, AlertOctagon, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OnboardingHandoff() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState({ stage: 'exchanging', error: null, payload: null });

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setState({ stage: 'error', error: 'Missing magic-link token in the URL.', payload: null });
      return;
    }
    (async () => {
      try {
        const r = await axios.post(`${API}/api/onboarding/exchange-magic-link`, { token });
        // Mirror the regular login persistence so navigating /dashboard works
        // immediately without re-asking for credentials.
        if (r.data?.access_token) {
          localStorage.setItem('token', r.data.access_token);
        }
        if (r.data?.user) {
          localStorage.setItem('user', JSON.stringify(r.data.user));
        }
        setState({ stage: 'ready', error: null, payload: r.data });
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || 'Magic link could not be verified.';
        setState({ stage: 'error', error: detail, payload: null });
      }
    })();
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-950 via-emerald-900 to-slate-900 px-4 py-12"
      data-testid="onboarding-handoff-page"
    >
      <Card className="max-w-md w-full shadow-2xl">
        <CardContent className="p-8">

          {state.stage === 'exchanging' && (
            <div className="text-center space-y-4" data-testid="onboarding-loading">
              <Loader2 className="h-12 w-12 text-emerald-600 mx-auto animate-spin" />
              <h1 className="text-xl font-display font-semibold text-slate-900">
                Spinning up your workspace…
              </h1>
              <p className="text-sm text-muted-foreground">Verifying your magic link.</p>
            </div>
          )}

          {state.stage === 'error' && (
            <div className="text-center space-y-4" data-testid="onboarding-error">
              <div className="h-14 w-14 rounded-full bg-red-100 mx-auto flex items-center justify-center">
                <AlertOctagon className="h-7 w-7 text-red-600" />
              </div>
              <h1 className="text-xl font-display font-semibold text-slate-900">Magic link couldn&apos;t be verified</h1>
              <p className="text-sm text-muted-foreground">
                {typeof state.error === 'string' ? state.error : 'Your link may have expired (links last 24 hours).'}
              </p>
              <div className="flex flex-col gap-2 pt-2">
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                  data-testid="onboarding-error-login-link"
                >
                  Log in with my password
                </Link>
                <Link to="/get-started" className="text-sm text-muted-foreground hover:text-slate-900 underline">
                  Or start a new signup
                </Link>
              </div>
            </div>
          )}

          {state.stage === 'ready' && (
            <div className="text-center space-y-5" data-testid="onboarding-success">
              <div className="h-16 w-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-display font-bold text-slate-900">
                You&apos;re in, {state.payload?.user?.name?.split(' ')?.[0] || 'Founder'}!
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Your academy is live on Out-Play. Hop into your dashboard to add your first
                batch, print your WhatsApp QR poster, and start enrolling students.
              </p>

              <ul className="text-left text-xs space-y-2 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <li className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                  Workspace provisioned
                </li>
                <li className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                  Owner account created — {state.payload?.user?.email}
                </li>
                <li className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                  Welcome email + setup guide sent
                </li>
              </ul>

              <Button
                onClick={() => navigate('/onboarding/wizard')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-6"
                data-testid="onboarding-enter-dashboard-btn"
              >
                <ArrowRight className="h-5 w-5 mr-2" />
                Continue to Setup Wizard
              </Button>
              <p className="text-[11px] text-muted-foreground pt-2">
                Your temporary password was emailed to you separately — you&apos;ll be asked to
                set a new one the first time you log out and back in.
              </p>
            </div>
          )}

        </CardContent>
      </Card>
    </div>
  );
}
